import React, { useContext, useEffect, useRef, useState } from "react";
import { Redirect, Route } from "react-router-dom";
import {
  IonApp,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonLabel,
  IonMenu,
  IonMenuToggle,
  IonPage,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTitle,
  IonToolbar,
  setupIonicReact,
  IonList,
  IonItem,
  IonModal,
  IonNav,
  IonTabs,
  IonAlert,
} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import Home from "./pages/Home";
import { useTranslation } from "react-i18next";
import {
  cafeOutline,
  compassOutline,
  logOutOutline,
  personCircleOutline,
  readerOutline,
  happyOutline,
  settingsOutline,
  flameOutline,
  downloadOutline,
} from "ionicons/icons";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

// Ionic v8 dark theme support:
// import '@ionic/react/css/palettes/dark.always.css';
import "@ionic/react/css/palettes/dark.class.css";
// import '@ionic/react/css/palettes/dark.system.css';

/* Flavoria's Customized palettes */
import "./theme/Flavoria.scss";

import GlobalContext from "./GlobalContext";
import FlavoriaHeaderBar from "./components/FlavoriaHeaderBar";
import Lunchlist from "./pages/Lunchlist";
import Benefits from "./pages/Benefits";
import Research from "./pages/Research";
import Meals from "./pages/Meals";
import StaticPage from "./pages/StaticPage";
import LoginPage from "./pages/loginPages/LoginPage";
import ConsentFormPage, {
  ResearchType,
} from "./pages/loginPages/ConsentFormPage";
import logout from "./logout";
import QR from "./pages/QR";
import GrillQueue from "./pages/GrillQueue";
import MyFlavoriaText from "./assets/my_flavoria_text_white.png";
import { rootPath } from "./Utils";

import myFlavoriaIcon from "./assets/icon/svgicons/my.svg";
import benefitsIcon from "./assets/icon/svgicons/benefits.svg";
import restaurantIcon from "./assets/icon/svgicons/restaurant.svg";
import researchIcon from "./assets/icon/svgicons/research.svg";
import mealsIcon from "./assets/icon/svgicons/meals.svg";
import SettingsDialog from "./pages/SettingsDialog";
import getSelfID from "./utils/getSelfId";
import i18next from "./i18n";
import getFlavoriaLang from "./utils/flavoriaLang";
import { isInstalledApp } from "./platform/abstraction";
import { isUpToDate } from "./platform/versionCheck";
import { showAddToHomeScreen } from "./utils/addToHomeScreen";
import buildInfo from "../package.json"
import SoftwareNotification from "./components/SoftwareNotification";
import LoginGuard from "./components/LoginGuard";
import BackendFactory from "./API/BackendFactory";
import { UserInfoObject } from "./API/lib/JsonUtils";
import { consumeGoogleSSOCallbackResult } from "./utils/googleSSO";


function getStylePrefrence() {
  const pref = localStorage.getItem("stylePrefrence");
  if (pref === "md" || pref === "ios") return pref;
  else return undefined;
}

setupIonicReact({ mode: getStylePrefrence() });

function AppContents() {
  const userContext = useContext(GlobalContext);
  const { t } = useTranslation();
  const modal = useRef<HTMLIonModalElement>(null);
  const [modal_open, set_modal_open] = useState(false);
  const [static_content, set_static_content] = useState("");
  const [notification_msg, set_notification_msg] = useState("");
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [settings_open, set_settings_open] = useState(false);
  const [ssoError, setSsoError] = useState("");

  function apptype() {return isInstalledApp() ? "native" : "pwa"}

  function appCookieDomain() {
    const host = window.location.hostname;
    if (host === "myflavoria.fi" || host.endsWith(".myflavoria.fi")) {
      return ";Domain=myflavoria.fi";
    }
    return "";
  }

  useEffect(() => {
    if (import.meta.env.MODE !== "production") {
      document.title = "My Flavoria Beta";
    }

    document.cookie = "appversion="+buildInfo.version+ ";path=/version"+appCookieDomain()
    document.cookie = "apptype="+apptype()+";path=/version"+appCookieDomain()

      //setupPushNotifications();
      isUpToDate().then((result) => {
        if(!result && isInstalledApp()) {
          set_notification_msg(t("New Version is available. Please update your My Flavoria® app."))
        }
      }).catch((err) => console.warn("isUpToDate check failed:", err))
    
  },[])

  useEffect(() => {
    const ssoResult = consumeGoogleSSOCallbackResult();
    if (ssoResult === null) return;

    if (ssoResult === "error") {
      setSsoError(
        t("Google sign-in failed. Please try again or use your email address.")
      );
      setShowLoginModal(true);
      return;
    }

    BackendFactory.getBackend()
      .getUserInfo()
      .then((result) => {
        const user = new UserInfoObject(result).getUserInfo();
        const email = user.email ?? "";
        localStorage.setItem("email", email);
        userContext.setEmail(email);

        if (user.termsVersion >= 5) {
          localStorage.setItem("agreementAccepted", "true");
          localStorage.setItem("acceptedResearchType", "3");
          localStorage.setItem("wannaParticipate", "true");
          userContext.setAgreementAccepted(true);
          userContext.setAcceptedResearchType(3);
          userContext.setWannaParticipate(true);
        } else {
          setShowLoginModal(true);
          setTimeout(() => {
            document.querySelector("ion-nav")?.push(() => <ConsentFormPage />);
          }, 100);
        }
      })
      .catch(() => {
        setSsoError(
          t("Could not finish Google sign-in. Please try again or use your email address.")
        );
        setShowLoginModal(true);
      });
  }, [])

  function initializeDarkPalette(isDark: boolean) {
    document.documentElement.classList.toggle("ion-palette-dark", isDark);
  }

  function useDarkTheme(): boolean {
    if (userContext.themeDarknessPrefrence === "light") {
      return false;
    } else if (userContext.themeDarknessPrefrence === "dark") {
      return true;
    } else {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
  }

  useEffect(() => {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", () => initializeDarkPalette(useDarkTheme()));
  }, []);

  function loggedIn() {
    return (
      userContext.agreementAccepted &&
      userContext.wannaParticipate &&
      userContext.acceptedResearchType !== ResearchType.NONE
    );
  }

  useEffect(() => {
    if (loggedIn() && showLoginModal) {
      setShowLoginModal(false);
    }
  }, [userContext.agreementAccepted, userContext.wannaParticipate, userContext.acceptedResearchType]);

  initializeDarkPalette(useDarkTheme());

  const isLoggedIn = loggedIn();

  return (
    <>
      <FlavoriaHeaderBar
        subtitle={t(userContext.activePage)}
        loggedIn={isLoggedIn}
        onLoginRequest={() => setShowLoginModal(true)}
      ></FlavoriaHeaderBar>

      <IonMenu contentId="mainpage">
        <IonHeader>
          <IonToolbar color="secondary">
            <IonTitle>
              <div>
                <img
                  style={{ paddingTop: "5px" }}
                  height="40"
                  src={MyFlavoriaText}
                />
              </div>
            </IonTitle>
            {isLoggedIn && (
              <IonButtons slot="end">
                <IonMenuToggle>
                  <IonButton
                    onClick={() => {
                      logout(userContext);
                    }}
                  >
                    <IonIcon aria-hidden="true" icon={logOutOutline} />
                  </IonButton>
                </IonMenuToggle>
              </IonButtons>
            )}
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <IonList style={{ paddingTop: "0px" }}>
            {isLoggedIn && (
              <IonItem color="secondary" lines="none">
                <IonIcon
                  aria-hidden="true"
                  icon={personCircleOutline}
                  slot="start"
                ></IonIcon>
                <IonLabel>{userContext.email}</IonLabel>
              </IonItem>
            )}
            <IonMenuToggle>
              <IonItem
                button
                onClick={() => {
                  set_static_content("restaurant-info");
                  set_modal_open(true);
                }}
              >
                <IonIcon
                  aria-hidden="true"
                  icon={cafeOutline}
                  slot="start"
                ></IonIcon>
                <IonLabel>{t("Restaurant Info")}</IonLabel>
              </IonItem>
            </IonMenuToggle>
            <IonMenuToggle>
              <IonItem
                button
                onClick={() => {
                  set_static_content("instructions");
                  set_modal_open(true);
                }}
              >
                <IonIcon
                  aria-hidden="true"
                  icon={compassOutline}
                  slot="start"
                ></IonIcon>
                <IonLabel>{t("Guide")}</IonLabel>
              </IonItem>
            </IonMenuToggle>
            <IonMenuToggle>
              <IonItem button href={`${rootPath}/grillqueue`}>
                <IonIcon
                  aria-hidden="true"
                  icon={flameOutline}
                  slot="start"
                ></IonIcon>
                <IonLabel>{t("Grill Queue")}</IonLabel>
              </IonItem>
            </IonMenuToggle>
            <IonMenuToggle>
              <IonItem
                button
                onClick={() => {
                  set_static_content("terms-5");
                  set_modal_open(true);
                }}
              >
                <IonIcon
                  aria-hidden="true"
                  icon={readerOutline}
                  slot="start"
                ></IonIcon>
                <IonLabel>{t("End User Agreement")}</IonLabel>
              </IonItem>
            </IonMenuToggle>
            <IonMenuToggle>
              <IonItem button onClick={() => set_settings_open(true)}>
                <IonIcon
                  aria-hidden="true"
                  icon={settingsOutline}
                  slot="start"
                ></IonIcon>
                <IonLabel>{t("Settings")}</IonLabel>
              </IonItem>
              <SettingsDialog
                isOpen={settings_open}
                onDidDismiss={() => set_settings_open(false)}
              />
            </IonMenuToggle>
            <IonMenuToggle>
              <IonItem button onClick={() => showAddToHomeScreen()}>
                <IonIcon
                  aria-hidden="true"
                  icon={downloadOutline}
                  slot="start"
                ></IonIcon>
                <IonLabel>{t("Install the App")}</IonLabel>
              </IonItem>
            </IonMenuToggle>
            <IonMenuToggle>
              <IonItem
                href={"https://redcap.utu.fi/surveys/?s=HLX4LFH4C4CF4TXR&__lang="+(getFlavoriaLang(i18next.language))+"&flavoriaid="+getSelfID()}
                target="blank"
              >
                <IonIcon icon={happyOutline} slot="start"></IonIcon>
                <IonLabel>{t("Give feedback")}</IonLabel>
              </IonItem>
            </IonMenuToggle>
            {isLoggedIn && (
              <IonMenuToggle>
                <IonItem
                  button
                  onClick={() => {
                    logout(userContext);
                  }}
                >
                  <IonIcon
                    aria-hidden="true"
                    icon={logOutOutline}
                    slot="start"
                  ></IonIcon>
                  <IonLabel>{t("Log Out")}</IonLabel>
                </IonItem>
              </IonMenuToggle>
            )}
          </IonList>
        </IonContent>
      </IonMenu>

      <IonPage id="mainpage">
        <IonTabs>
          <IonRouterOutlet>
            <Route exact path={`${rootPath}/home`}>
              <Home onLoginRequest={() => setShowLoginModal(true)} />
            </Route>
            <Route exact path={`${rootPath}/benefits`}>
              <LoginGuard onLoginRequest={() => setShowLoginModal(true)}>
                <Benefits />
              </LoginGuard>
            </Route>
            <Route exact path={`${rootPath}/lunchlist`}>
              <Lunchlist />
            </Route>
            <Route exact path={`${rootPath}/research`}>
              <LoginGuard onLoginRequest={() => setShowLoginModal(true)}>
                <Research />
              </LoginGuard>
            </Route>
            <Route exact path={`${rootPath}/meals`}>
              <LoginGuard onLoginRequest={() => setShowLoginModal(true)}>
                <Meals />
              </LoginGuard>
            </Route>
            <Route exact path={`${rootPath}/qr`}>
              <LoginGuard onLoginRequest={() => setShowLoginModal(true)}>
                <QR />
              </LoginGuard>
            </Route>
            <Route exact path={`${rootPath}/grillqueue`}>
              <GrillQueue />
            </Route>
            <Route exact path={`${rootPath}/`}>
              <Redirect to={`${rootPath}/home`} />
            </Route>
            <Route exact path={`/`}>
              <Redirect to={`${rootPath}/home`} />
            </Route>
          </IonRouterOutlet>
          <IonTabBar slot="bottom" className="bottomMenuBar">
            <IonTabButton tab="tab1" href={`${rootPath}/home`}>
              <IonIcon aria-hidden="true" icon={myFlavoriaIcon} />
              <IonLabel className="tabButtonLabel">{t("My Flavoria")}</IonLabel>
            </IonTabButton>
            <IonTabButton
              tab="tab2"
              href={isLoggedIn ? `${rootPath}/benefits` : undefined}
              onClick={isLoggedIn ? undefined : (e) => { e.preventDefault(); setShowLoginModal(true); }}
            >
              <IonIcon aria-hidden="true" icon={benefitsIcon} />
              <IonLabel className="tabButtonLabel">{t("Benefits")}</IonLabel>
            </IonTabButton>
            <IonTabButton tab="tab3" href={`${rootPath}/lunchlist`}>
              <IonIcon aria-hidden="true" icon={restaurantIcon} />
              <IonLabel className="tabButtonLabel">{t("Lunch List")}</IonLabel>
            </IonTabButton>
            <IonTabButton
              tab="tab4"
              href={isLoggedIn ? `${rootPath}/research` : undefined}
              onClick={isLoggedIn ? undefined : (e) => { e.preventDefault(); setShowLoginModal(true); }}
            >
              <IonIcon aria-hidden="true" icon={researchIcon} />
              <IonLabel className="tabButtonLabel">{t("Research")}</IonLabel>
            </IonTabButton>
            <IonTabButton
              tab="tab5"
              href={isLoggedIn ? `${rootPath}/meals` : undefined}
              onClick={isLoggedIn ? undefined : (e) => { e.preventDefault(); setShowLoginModal(true); }}
            >
              <IonIcon aria-hidden="true" icon={mealsIcon} />
              <IonLabel className="tabButtonLabel">{t("Data")}</IonLabel>
            </IonTabButton>
          </IonTabBar>
        </IonTabs>
      </IonPage>

      <IonModal
        ref={modal}
        isOpen={modal_open}
        onDidDismiss={() => set_modal_open(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="end">
              <IonButton onClick={() => modal.current?.dismiss()}>
                {t("Close")}
              </IonButton>
            </IonButtons>
            <IonTitle>{t(static_content)}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <StaticPage pageName={static_content}></StaticPage>
      </IonModal>
      <SoftwareNotification onDidDismiss={() => {set_notification_msg("")}} showMealExtraPopup={notification_msg != ""} message={notification_msg}/>

      <IonModal
        isOpen={showLoginModal}
        onDidDismiss={() => setShowLoginModal(false)}
      >
        <IonNav id="login-nav" root={() => <LoginPage onDismiss={() => setShowLoginModal(false)} />}></IonNav>
      </IonModal>

      <IonAlert
        isOpen={ssoError !== ""}
        onDidDismiss={() => setSsoError("")}
        header={t("Sign in with Google")}
        message={ssoError}
        buttons={[t("Close")]}
      />
    </>
  );
}

export function App() {
  return (
    <IonApp>
      <IonReactRouter>
        <AppContents />
      </IonReactRouter>
    </IonApp>
  );
}

export default App;
